import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Button,
  Checkbox,
  cn,
  Input,
  Label,
  Sheet,
  SheetBody,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@nexus/ui";
import type {
  ChangelogCandidateEntry,
  ChangelogCandidatesPage,
} from "@/features/changelog/api/changelog.api";
import { useSprintConfig } from "@/features/changelog/hooks/useChangelogSettings";
import {
  buildTicketUrl,
  getTimestampDisplay,
} from "@/features/changelog/lib/changelog-display";
import { CHANGELOG_LIMITS } from "@/features/changelog/lib/changelog-edit";
import {
  buildTemplates,
  buildTimeline,
  formatWindow,
  itemIdsInWindow,
  type ChangelogTemplate,
  type ChangelogTemplateId,
  type TimelineDay,
} from "@/features/changelog/lib/changelog-templates";
import { ChangelogDeployTimeline } from "./ChangelogDeployTimeline";
import { SectionChip } from "./shared/ChangelogSectionList";

interface ChangelogComposeSheetProps {
  isOpen: boolean;
  /** Undefined while the candidate list loads. */
  candidates: ChangelogCandidatesPage | undefined;
  /** Ticked on open, for a sheet opened from deploys the admin already picked. */
  initialItemIds?: readonly string[];
  isLoading: boolean;
  isComposing: boolean;
  composeError: string | null;
  onOpenChange: (open: boolean) => void;
  onCompose: (input: { title: string; itemIds: string[] }) => void;
}

/**
 * Curation happens here rather than after the merge, because the model cannot
 * be told which internal plumbing change is worth a stranger's attention and an
 * admin can. What is ticked is what the roundup is written from; the rest stays
 * in Slack, where the audience wanted the detail.
 */
export function ChangelogComposeSheet({
  isOpen,
  candidates,
  initialItemIds,
  isLoading,
  isComposing,
  composeError,
  onOpenChange,
  onCompose,
}: ChangelogComposeSheetProps) {
  const { t, i18n } = useTranslation();
  const sprint = useSprintConfig();

  const [title, setTitle] = useState("");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  // A template stops suggesting a name once the admin has typed their own.
  const [isTitleEdited, setIsTitleEdited] = useState(false);
  const [appliedTemplate, setAppliedTemplate] =
    useState<ChangelogTemplateId | null>(null);
  // Frozen per opening, so the "last 7 days" band cannot drift under a sheet
  // that has been left open.
  const [openedAt, setOpenedAt] = useState(() => new Date());

  // Nothing starts ticked unless the caller arrived with deploys already
  // picked. Pre-selecting on the sheet's own initiative would make the default
  // outcome "mail all of it", which is the firehose a roundup exists to avoid;
  // ticking what an admin selected row by row is the opposite of a default.
  useEffect(() => {
    if (!isOpen) return;
    setTitle(defaultTitle(i18n.language));
    setSelected(new Set(initialItemIds ?? []));
    setIsTitleEdited(false);
    setAppliedTemplate(null);
    setOpenedAt(new Date());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // A deploy that shipped nothing user-facing has nothing to tick, and listing
  // it only produced rows that could not be picked.
  const entries = useMemo(
    () => (candidates?.entries ?? []).filter((entry) => entry.items.length > 0),
    [candidates?.entries],
  );
  const totalItems = useMemo(
    () => entries.reduce((sum, entry) => sum + entry.items.length, 0),
    [entries],
  );

  const templates = useMemo(
    () => buildTemplates(entries, openedAt, i18n.language, sprint),
    [entries, openedAt, i18n.language, sprint],
  );
  const activeWindow =
    templates.find((template) => template.id === appliedTemplate)?.window ??
    null;
  const timeline = useMemo(
    () => buildTimeline(entries, activeWindow),
    [entries, activeWindow],
  );

  /**
   * Pressed means "this is the stretch on screen", not merely "the ticks happen
   * to match" — two templates covering the same quiet fortnight would otherwise
   * both light up. Deviating from the template un-presses it while the band
   * stays for context.
   */
  function isTemplateActive(template: ChangelogTemplate): boolean {
    if (appliedTemplate !== template.id) return false;
    const ids = itemIdsInWindow(entries, template.window);
    return ids.length === selected.size && ids.every((id) => selected.has(id));
  }

  function applyTemplate(template: ChangelogTemplate) {
    if (isTemplateActive(template)) {
      setSelected(new Set());
      setAppliedTemplate(null);
      return;
    }
    setSelected(new Set(itemIdsInWindow(entries, template.window)));
    setAppliedTemplate(template.id);
    if (!isTitleEdited) setTitle(template.title);
  }

  function toggleDay(day: TimelineDay<ChangelogCandidateEntry>) {
    const ids = day.entries.flatMap((entry) =>
      entry.items.map((item) => item.id),
    );
    const isAllPicked = ids.every((id) => selected.has(id));
    setSelected((current) => {
      const next = new Set(current);
      for (const id of ids) {
        if (isAllPicked) next.delete(id);
        else next.add(id);
      }
      return next;
    });
  }

  function toggleItem(itemId: string) {
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(itemId)) next.delete(itemId);
      else next.add(itemId);
      return next;
    });
  }

  function toggleEntry(entry: ChangelogCandidateEntry) {
    const ids = entry.items.map((item) => item.id);
    const isAllPicked = ids.every((id) => selected.has(id));
    setSelected((current) => {
      const next = new Set(current);
      for (const id of ids) {
        if (isAllPicked) next.delete(id);
        else next.add(id);
      }
      return next;
    });
  }

  const trimmedTitle = title.trim();
  const canCompose =
    trimmedTitle.length > 0 && selected.size > 0 && !isComposing && !isLoading;

  return (
    <Sheet open={isOpen} onOpenChange={onOpenChange}>
      <SheetContent>
        <SheetHeader className="gap-1 pr-12">
          <SheetTitle>
            {t("admin.changelog.compose.title", "Compose a roundup")}
          </SheetTitle>
          <SheetDescription>
            {t(
              "admin.changelog.compose.description",
              "Pick what is worth telling everyone. The picked items are rewritten into one message.",
            )}
          </SheetDescription>
        </SheetHeader>

        <SheetBody className="flex flex-col gap-5">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="digest-title">
              {t("admin.changelog.compose.titleLabel", "Title")}
            </Label>
            <Input
              id="digest-title"
              value={title}
              maxLength={CHANGELOG_LIMITS.title}
              placeholder={t(
                "admin.changelog.compose.titlePlaceholder",
                "Week of 4 August",
              )}
              onChange={(event) => {
                setIsTitleEdited(true);
                setTitle(event.target.value);
              }}
            />
          </div>

          {isLoading ? (
            <p className="m-0 text-xs text-muted-foreground">
              {t(
                "admin.changelog.compose.loading",
                "Loading what has shipped…",
              )}
            </p>
          ) : entries.length === 0 ? (
            <p className="m-0 max-w-[52ch] text-xs text-muted-foreground">
              {t(
                "admin.changelog.compose.empty",
                "Every approved release is already in a roundup. Nothing left to pick.",
              )}
            </p>
          ) : (
            <>
              <section className="flex flex-col gap-3 rounded-md border border-border-subtle p-3">
                <div className="flex flex-col gap-1">
                  <span className="text-[11px] uppercase tracking-wider text-muted-foreground">
                    {t("admin.changelog.compose.template.label", "Cover")}
                  </span>
                  <div className="flex flex-wrap gap-1.5">
                    {templates.map((template) => {
                      const isEmpty = template.itemCount === 0;

                      return (
                        <Button
                          key={template.id}
                          variant={
                            isTemplateActive(template) ? "default" : "outline"
                          }
                          size="sm"
                          className="h-7 gap-1.5 rounded-sm text-xs"
                          disabled={isComposing || isEmpty}
                          aria-pressed={isTemplateActive(template)}
                          title={formatWindow(template.window, i18n.language)}
                          onClick={() => applyTemplate(template)}
                        >
                          {/* The sprint chip names the sprint it found; the
                              other two are fixed spans and translate. */}
                          {template.id === "sprint"
                            ? template.title
                            : t(
                                `admin.changelog.compose.template.${template.id}`,
                                { defaultValue: template.id },
                              )}
                          <span className="tabular-nums opacity-70">
                            {template.entryCount}
                          </span>
                        </Button>
                      );
                    })}
                  </div>
                </div>

                {timeline ? (
                  <ChangelogDeployTimeline
                    timeline={timeline}
                    window={activeWindow}
                    selected={selected}
                    isDisabled={isComposing}
                    onToggleDay={toggleDay}
                  />
                ) : null}

                <p className="m-0 text-[11px] text-muted-foreground">
                  {activeWindow
                    ? t(
                        "admin.changelog.compose.template.covering",
                        "Covering {{range}}. Click a day to add or drop it.",
                        { range: formatWindow(activeWindow, i18n.language) },
                      )
                    : t(
                        "admin.changelog.compose.template.hint",
                        "Pick a stretch, or click days on the chart.",
                      )}
                </p>
              </section>

              {/* Nothing is ticked to begin with, so the list needs a way out of
                  the zero state that is not twenty clicks. */}
              <div className="flex items-center justify-between">
                <span className="text-[11px] uppercase tracking-wider text-muted-foreground">
                  {t("admin.changelog.compose.items", "What has shipped")}
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 rounded-sm text-xs"
                  disabled={isComposing}
                  onClick={() => {
                    setAppliedTemplate(null);
                    setSelected(
                      selected.size === totalItems
                        ? new Set()
                        : new Set(
                            entries.flatMap((entry) =>
                              entry.items.map((item) => item.id),
                            ),
                          ),
                    );
                  }}
                >
                  {selected.size === totalItems
                    ? t("admin.changelog.compose.clear", "Clear")
                    : t("admin.changelog.compose.selectAll", "Select all")}
                </Button>
              </div>

              {entries.map((entry) => {
                const isAllPicked = entry.items.every((item) =>
                  selected.has(item.id),
                );
                const deployed = getTimestampDisplay(
                  entry.deployedAt,
                  i18n.language,
                );

                return (
                  <section
                    key={entry.versionTag}
                    className="flex flex-col gap-1"
                  >
                    <div className="flex items-center gap-2.5 border-b border-border-subtle pb-1.5">
                      <Checkbox
                        checked={isAllPicked}
                        disabled={isComposing}
                        aria-label={t(
                          "admin.changelog.compose.pickDeploy",
                          "Pick every item from {{version}}",
                          { version: entry.versionTag },
                        )}
                        onCheckedChange={() => toggleEntry(entry)}
                      />
                      <span className="font-mono text-xs">
                        {entry.versionTag}
                      </span>
                      <span className="flex-1" />
                      <span className="text-[11px] tabular-nums text-muted-foreground">
                        {deployed.date}
                      </span>
                    </div>

                    <ul className="m-0 flex list-none flex-col p-0">
                      {entry.items.map((item) => (
                        <li key={item.id}>
                          <label
                            className={cn(
                              "flex items-start gap-2.5 rounded-sm px-1.5 py-2 text-xs transition-colors",
                              isComposing
                                ? "cursor-not-allowed opacity-60"
                                : "cursor-pointer hover:bg-muted",
                            )}
                          >
                            <Checkbox
                              className="mt-0.5"
                              checked={selected.has(item.id)}
                              disabled={isComposing}
                              onCheckedChange={() => toggleItem(item.id)}
                            />
                            <SectionChip
                              sectionKey={item.section}
                              className="mt-0.5"
                            />
                            <span className="min-w-0 flex-1">
                              <span className="font-medium">{item.topic}:</span>{" "}
                              {item.description}
                              {item.ticket ? (
                                <>
                                  {" "}
                                  <a
                                    href={buildTicketUrl(item.ticket)}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="whitespace-nowrap text-primary underline-offset-4 hover:underline"
                                    onClick={(event) => event.stopPropagation()}
                                  >
                                    {item.ticket}
                                  </a>
                                </>
                              ) : null}
                            </span>
                          </label>
                        </li>
                      ))}
                    </ul>
                  </section>
                );
              })}
            </>
          )}

          {composeError ? (
            <div className="rounded-md border border-error-border bg-error-subtle px-4 py-3 text-xs text-error-subtle-foreground">
              {composeError}
            </div>
          ) : null}
        </SheetBody>

        <SheetFooter className="justify-between px-6">
          <span className="text-[11px] tabular-nums text-muted-foreground">
            {t("admin.changelog.compose.picked", {
              count: selected.size,
              total: totalItems,
            })}
          </span>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              className="h-8 rounded-sm text-xs"
              disabled={isComposing}
              onClick={() => onOpenChange(false)}
            >
              {t("admin.changelog.compose.cancel", "Cancel")}
            </Button>
            <Button
              size="sm"
              className="h-8 rounded-sm text-xs"
              disabled={!canCompose}
              onClick={() =>
                onCompose({ title: trimmedTitle, itemIds: [...selected] })
              }
            >
              {isComposing
                ? t("admin.changelog.compose.composing", "Merging…")
                : t("admin.changelog.compose.confirm", {
                    count: selected.size,
                  })}
            </Button>
          </div>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}

/** A week label is what these are almost always called, so it is offered ready-made. */
function defaultTitle(locale: string): string {
  return `Week of ${new Date().toLocaleDateString(locale, { day: "numeric", month: "long" })}`;
}
